import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, emailAccountsApi, relTime } from '../lib/api'
import { useToast } from '../components/Toast'

/**
 * Hosted mailboxes on the customer's own domains: receive (MX to us, IMAP, webmail) and send
 * (SMTP with the same address and password).
 */
export function EmailAccounts() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading } = useQuery({ queryKey: ['email-accounts'], queryFn: emailAccountsApi.list })
  const [local, setLocal] = useState('')
  const [domain, setDomain] = useState('')
  const [password, setPassword] = useState('')
  const [resetFor, setResetFor] = useState<number | null>(null)
  const [newPw, setNewPw] = useState('')

  const refresh = () => qc.invalidateQueries({ queryKey: ['email-accounts'] })
  const fail = (e: unknown) => toast.error((e as ApiError).message)
  const create = useMutation({
    mutationFn: () => emailAccountsApi.create({ local_part: local, domain: domain || verified[0]?.name || '', password }),
    onSuccess: (r) => { toast.success(`${r.address} created.`); setLocal(''); setPassword(''); refresh() },
    onError: fail,
  })
  const update = useMutation({
    mutationFn: (v: { id: number; password?: string; enabled?: boolean }) => emailAccountsApi.update(v.id, v),
    onSuccess: () => { toast.success('Saved.'); setResetFor(null); setNewPw(''); refresh() },
    onError: fail,
  })
  const remove = useMutation({
    mutationFn: (id: number) => emailAccountsApi.remove(id),
    onSuccess: () => { toast.success('Email account deleted.'); refresh() },
    onError: fail,
  })

  if (isLoading || !data) return <div className="skeleton" style={{ height: 320 }} />
  const verified = data.domains.filter(d => d.verified)
  const atLimit = data.limits.used >= data.limits.max
  const s = data.settings

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Email accounts</h1>
          <p className="lede">Professional mailboxes on your own domain — read and send from webmail, your phone or Outlook.
            {' '}{data.limits.used} of {data.limits.max} accounts used.</p>
        </div>
        <a className="button" href={s.webmail_url} target="_blank" rel="noreferrer">Open webmail ↗</a>
      </div>

      {verified.length === 0 && (
        <div className="alert info">Add and verify a domain on the <Link to="/domains">Domains</Link> page first, then create email accounts on it.</div>
      )}

      {verified.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 style={{ marginTop: 0 }}>New email account</h2>
          <form className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-end', gap: 10 }}
                onSubmit={e => { e.preventDefault(); create.mutate() }}>
            <div className="field" style={{ flex: '2 1 280px', marginBottom: 0 }}>
              <label htmlFor="ea-local">Address</label>
              <div className="row" style={{ gap: 6 }}>
                <input id="ea-local" required value={local} placeholder="ali" onChange={e => setLocal(e.target.value.replace(/[@\s]/g, '').toLowerCase())} />
                <span className="muted">@</span>
                <select value={domain || verified[0]?.name} onChange={e => setDomain(e.target.value)}>
                  {verified.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
                </select>
              </div>
            </div>
            <div className="field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label htmlFor="ea-pw">Password (10+ characters)</label>
              <input id="ea-pw" type="password" required minLength={10} autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            <button type="submit" disabled={create.isPending || atLimit}>{create.isPending ? 'Creating…' : 'Create account'}</button>
          </form>
          {atLimit && <p className="small muted" style={{ marginBottom: 0 }}>Your plan's account limit is reached. <Link to="/billing">Talk to us</Link> to add more.</p>}
        </div>
      )}

      {data.data.length > 0 && (
        <div className="card flush" style={{ marginBottom: 20 }}>
          <table className="data">
            <thead><tr><th>Address</th><th>Storage</th><th>Status</th><th>Created</th><th /></tr></thead>
            <tbody>
              {data.data.map(a => (
                <tr key={a.id}>
                  <td className="mono small">{a.address}</td>
                  <td className="small">{a.used_mb} MB of {a.quota_mb >= 1024 ? `${(a.quota_mb / 1024).toFixed(0)} GB` : `${a.quota_mb} MB`}</td>
                  <td>{a.enabled ? <span className="badge ok">Active</span> : <span className="badge neutral">Disabled</span>}</td>
                  <td className="small muted">{relTime(a.created_at)}</td>
                  <td className="right">
                    {resetFor === a.id ? (
                      <form className="row" style={{ gap: 6, justifyContent: 'flex-end' }} onSubmit={e => { e.preventDefault(); update.mutate({ id: a.id, password: newPw }) }}>
                        <input type="password" minLength={10} required placeholder="New password" value={newPw} onChange={e => setNewPw(e.target.value)} style={{ maxWidth: 180 }} />
                        <button className="small" type="submit">Save</button>
                        <button className="ghost small" type="button" onClick={() => setResetFor(null)}>Cancel</button>
                      </form>
                    ) : (
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <button className="ghost small" onClick={() => { setResetFor(a.id); setNewPw('') }}>Reset password</button>
                        <button className="ghost small" onClick={() => update.mutate({ id: a.id, enabled: !a.enabled })}>{a.enabled ? 'Disable' : 'Enable'}</button>
                        <button className="ghost small" onClick={() => { if (confirm(`Delete ${a.address}? Its mail is kept for 30 days, then removed.`)) remove.mutate(a.id) }}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 }}>
        <div className="card">
          <h2 style={{ marginTop: 0 }}>1. Receive mail: point your MX records to us</h2>
          <p className="small muted" style={{ marginTop: 0 }}>In your DNS, replace the existing MX records of the domain with these two. Mail starts arriving within minutes of the change.</p>
          <table className="kv"><tbody>
            {s.mx.map(m => <tr key={m.host}><td>MX · priority {m.priority}</td><td className="mono">{m.host}</td></tr>)}
          </tbody></table>
          {data.domains.length > 0 && (
            <>
              <h3 style={{ marginBottom: 6 }}>Your domains</h3>
              {data.domains.map(d => (
                <div key={d.name} className="row small" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
                  <span className="mono">{d.name}</span>
                  {d.mx_ok ? <span className="badge ok">MX points to us</span>
                    : <span className="badge pending" title={d.mx_current.join(', ') || 'no MX record'}>MX not set up yet</span>}
                </div>
              ))}
            </>
          )}
        </div>
        <div className="card">
          <h2 style={{ marginTop: 0 }}>2. Use it anywhere</h2>
          <p className="small muted" style={{ marginTop: 0 }}>Username is the full email address; password is the one you set here.</p>
          <table className="kv"><tbody>
            <tr><td>Webmail</td><td><a href={s.webmail_url} target="_blank" rel="noreferrer">{s.webmail_url.replace('https://', '')}</a></td></tr>
            <tr><td>Incoming (IMAP)</td><td className="mono">{s.imap.host} · {s.imap.port} · {s.imap.security}</td></tr>
            <tr><td>Outgoing (SMTP)</td><td className="mono">{s.smtp.host} · {s.smtp.port} · {s.smtp.security}</td></tr>
          </tbody></table>
          <p className="small muted">Sent mail counts toward your account's daily sending allowance, like every other email.</p>
        </div>
      </div>
    </div>
  )
}
